import React, { useMemo, useContext } from 'react';
import { useAuthorizations } from 'hooks';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { Button, IconButton, TextField } from '@totalsoft/rocket-ui';
import { DateTime } from '@ts-private/rocket-extensions';
import PropTypes from 'prop-types';
import { valueOrDefault } from 'utils/functions';
import { emptyString } from 'constants/defaultValues';
import { get, set } from '@totalsoft/change-tracking-react';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import PropertiesComponent from 'components/common/property/PropertiesComponent';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';
import AuthorizationCodes from 'features/common/constants/authorizationCodes';
import useConfirmDialog from 'features/common/hooks/useConfirmDialog';
import { DocumentActionsWrapper, DocumentTransitionsWrapper } from './AttachmentStyles';

const AttachmentComponent = ({
  attachment,
  maxSize,
  availableTransitions,
  actionInProgress,
  isDirty,
  onSave,
  onChangeState,
  onDelete,
  onUploadFile,
  onDownloadFile,
  onOpenDocStateTransitionsModal,
  onOpenHistoryModal,
  loadPropertyDetails,
  validation
}) => {
  const { globalReadOnly, documentTypeId, documentStateId } = useContext(InsuranceDataContext);
  const authorizations = useAuthorizations(
    [AuthorizationCodes.hasRightToEditUploadDocuments, AuthorizationCodes.hasRightToAddOrRemoveDocuments],
    documentTypeId,
    documentStateId
  );
  const { t } = useTranslation();
  const confirm = useConfirmDialog();
  const attachmentId = useMemo(() => attachment.documentAttachmentId |> get, [attachment]);
  const isExternal = useMemo(() => attachment.isExternal |> get, [attachment]);

  return (
    <Grid container spacing={2} px={1}>
      <Grid item xs={12}>
        <TextField
          id={`attachment_DocumentNameDetail_${attachmentId}`}
          fullWidth
          disabled
          name='requiredDocumentName'
          value={attachment.requiredDocumentName |> get |> valueOrDefault(emptyString)}
          label={t('Offer.Edit.AttachmentsData.DocumentName')}
          endAdornment={
            <>
              <IconButton
                id={`attachment_Upload_${attachmentId}`}
                variant='text'
                color='primary'
                size='small'
                tooltip={t('Tooltips.Upload', { size: maxSize })}
                disabled={
                  globalReadOnly({
                    controlName: 'Documents.Upload',
                    hasAuthorization: authorizations?.hasRightToEditUploadDocuments
                  }) ||
                  actionInProgress ||
                  isDirty ||
                  isExternal
                }
                onClick={() => onUploadFile(attachment.documentAttachmentId |> get)}
              >
                <CloudUploadIcon />
              </IconButton>
              <IconButton
                id={`attachment_Download_${attachmentId}`}
                variant='text'
                color='primary'
                size='small'
                tooltip={t('Tooltips.Download')}
                disabled={!(attachment.fileUniqueId |> get) || actionInProgress}
                onClick={() => onDownloadFile(attachment.fileUniqueId |> get)}
              >
                <CloudDownloadIcon />
              </IconButton>
              <IconButton
                id={`attachment_History_${attachmentId}`}
                variant='text'
                color='primary'
                size='small'
                tooltip={t('Tooltips.History')}
                disabled={!(attachment.fileUniqueId |> get) || actionInProgress || isExternal}
                onClick={() => onOpenHistoryModal(attachment |> get)}
              >
                <HistoryIcon />
              </IconButton>
            </>
          }
        />
      </Grid>
      <Grid item xs={12} md={4} sm={6}>
        <TextField
          id={`attachment_EntityDetail_${attachmentId}`}
          fullWidth
          disabled
          name='entity'
          value={`${attachment.entityType |> get |> valueOrDefault(emptyString)} ${
            attachment.entityTitle |> get |> valueOrDefault(emptyString)
          }`}
          label={t('Offer.Edit.AttachmentsData.Entity')}
        />
      </Grid>
      <Grid item xs={12} md={4} sm={6}>
        <TextField
          id={`attachment_StateDetail_${attachmentId}`}
          fullWidth
          disabled
          name='stateName'
          value={attachment.stateName |> get |> valueOrDefault(emptyString)}
          label={t('Offer.Edit.AttachmentsData.State')}
          endAdornment={
            <IconButton
              id={`attachment_TransitionHistory_${attachmentId}`}
              variant='text'
              color='primary'
              size='small'
              tooltip={t('Offer.Edit.AttachmentsData.TransitionHistory')}
              onClick={() => onOpenDocStateTransitionsModal(attachment |> get)}
            >
              <CompareArrowsIcon />
            </IconButton>
          }
        />
      </Grid>
      <Grid item xs={12} md={4} sm={6}>
        <TextField
          id={`attachment_UploadedBy_${attachmentId}`}
          fullWidth
          disabled
          name='uploadedBy'
          value={
            attachment.fileUniqueId |> get
              ? `${attachment.userName |> get |> valueOrDefault(emptyString)} / ${t('DATE', {
                  date: attachment.uploadedDate |> get
                })}`
              : emptyString
          }
          label={t('Offer.Edit.AttachmentsData.UploadedBy')}
        />
      </Grid>
      <Grid item xs={12} md={4} sm={6}>
        <DateTime
          id={`attachment_ValidityDate_${attachmentId}`}
          value={attachment.validityDate |> get}
          onChange={attachment.validityDate |> set}
          isValidDate={date => date.isSameOrAfter(new Date(), 'day')}
          timeFormat={false}
          editable={false}
          disabled={
            globalReadOnly({
              controlName: 'Documents.ValidityDate',
              hasAuthorization: authorizations?.hasRightToEditUploadDocuments
            }) || isExternal
          }
          fullWidth
          error={!isValid(validation.validityDate)}
          helperText={getErrors(validation.validityDate)}
          label={t('Offer.Edit.AttachmentsData.ValidityDate')}
        />
      </Grid>
      <PropertiesComponent
        propertiesLens={attachment.properties}
        validation={validation.properties}
        loadPropertyDetails={loadPropertyDetails}
        readOnly={globalReadOnly({ controlName: 'Documents.Properties' }) || isExternal}
      />
      <Grid item xs={12}>
        <TextField
          id={`attachment_Comments_${attachmentId}`}
          fullWidth
          label={t('Offer.Edit.AttachmentsData.Comments')}
          multiline
          maxRows='4'
          disabled={
            globalReadOnly({
              controlName: 'Documents.Comments',
              hasAuthorization: authorizations?.hasRightToEditUploadDocuments
            }) || isExternal
          }
          onChange={attachment.attachmentComment |> set}
          value={attachment.attachmentComment |> get |> valueOrDefault(emptyString)}
          error={!isValid(validation.attachmentComment)}
          helperText={getErrors(validation.attachmentComment)}
        />
      </Grid>
      {!isExternal && (
        <Grid item container xs={12} alignItems='center' justifyContent='space-between'>
          <DocumentTransitionsWrapper item>
            {availableTransitions.map((transition, idx) => (
              <Button
                id={`attachments_TransitionBtn_${idx}`}
                key={idx}
                disabled={
                  globalReadOnly({
                    controlName: 'Documents.Transition',
                    hasAuthorization: authorizations?.hasRightToEditUploadDocuments
                  }) ||
                  actionInProgress ||
                  isDirty
                }
                onClick={() => onChangeState(attachment.documentAttachmentId |> get, transition.finalStateId)}
              >
                {transition.finalStateName}
              </Button>
            ))}
          </DocumentTransitionsWrapper>
          <DocumentActionsWrapper item justifyContent={'flex-end'}>
            <IconButton
              id={`attachment_SaveBtn_${attachmentId}`}
              onClick={onSave}
              variant='text'
              color='primary'
              tooltip={t('Save')}
              disabled={globalReadOnly({ controlName: 'Documents' }) || actionInProgress || !isDirty}
            >
              <SaveIcon />
            </IconButton>
            <IconButton
              id={`attachment_Delete_${attachmentId}`}
              variant='text'
              color='error'
              tooltip={t('Delete')}
              onClick={() =>
                confirm(() => onDelete(attachment.documentAttachmentId |> get), {
                  title: t('Offer.Edit.AttachmentsData.DeleteTitle'),
                  content: t('Offer.Edit.AttachmentsData.DeleteDescription')
                })
              }
              disabled={
                globalReadOnly({
                  controlName: 'Documents',
                  hasAuthorization: authorizations?.hasRightToAddOrRemoveDocuments
                }) ||
                actionInProgress ||
                (attachment.mandatory |> get)
              }
            >
              <DeleteIcon />
            </IconButton>
          </DocumentActionsWrapper>
        </Grid>
      )}
    </Grid>
  );
};

AttachmentComponent.propTypes = {
  attachment: PropTypes.object.isRequired,
  maxSize: PropTypes.number,
  availableTransitions: PropTypes.array.isRequired,
  actionInProgress: PropTypes.bool.isRequired,
  isDirty: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onChangeState: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onUploadFile: PropTypes.func.isRequired,
  onDownloadFile: PropTypes.func.isRequired,
  onOpenDocStateTransitionsModal: PropTypes.func.isRequired,
  onOpenHistoryModal: PropTypes.func.isRequired,
  loadPropertyDetails: PropTypes.func.isRequired,
  validation: PropTypes.object.isRequired
};

export default AttachmentComponent;
